/*
 * Copyright © 2018-2026, GlobalVET AB
 *
 * All rights reserved. No part or the whole of this source code and the compiled program
 * may be reproduced, copied, distributed, disseminated to the public, adapted or transmitted
 * in any form or by any means, including photocopying, recording, or other electronic or
 * mechanical methods, without the prior written permission of GlobalVET AB. This source code
 * and the compiled program may only be used for the purposes of GlobalVET AB. This source code
 * and the compiled program shall be kept confidential and shall not be made public or made
 * available or disclosed to any unauthorized person. Any dispute or claim arising out of the
 * breach of these provisions shall be governed by and construed in accordance with the
 * laws of Sweden.
 */

import React, { ReactElement, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { strings } from "../../common/Strings/Strings";
import { useClinic } from "../../contexts/ClinicContext";
import { getGeneralError } from "../../util/helperFunctions";
import NumberFormatter from "../../util/NumberFormatter";
import LoaderInline from "../../components/LoaderInline";
import { ArrowDownTray } from "../../common/Icons/ArrowDownTray";
import Button from "../../components/Button";
import { downloadInventoryStockValuesPdf, printInventoryStockValuesPdf } from "../../util/FileUtils";
import AlertBox from "../../components/AlertBox";
import Tooltip from "../../components/Tooltip";
import { Printer } from "../../common/Icons/Printer";
import InventoryStatisticsApi from "../../api/InventoryStatisticsApi";
import { StockValuesResponse } from "../../models/inventory/statistics/StockValuesResponse";
import DatePickerInput from "../../components/ReactHookFormFields/ReactDateAndTimePickers/DatePickerInput";
import Select from "../../components/ReactHookFormFields/General/Select/Select";
import StatInfoTile from "./StatInfoTileComponent";
import logger from "../../util/logger";

interface StockValueForm {
  date?: Date;
  netOrGross: "net" | "gross";
}

const InventoryStockValue = (): ReactElement => {
  const [error, setError] = useState<string | null>(null);
  const [statistics, setStatistics] = useState<StockValuesResponse>();
  const [loading, setLoading] = useState<boolean>(true);
  const [pdfLoading, setPdfLoading] = useState<boolean>(false);
  const [reloadStatistics, setReloadStatistics] = useState<boolean>(false);

  const filterFormMethods = useForm<StockValueForm>({
    mode: "onChange",
    defaultValues: {
      date: new Date(),
      netOrGross: "net",
    },
  });
  const { getValues, control, register, watch } = filterFormMethods;
  const netOrGross = watch("netOrGross");

  const { clinic } = useClinic();

  useEffect(() => {
    const getStatistics = async () => {
      if (!clinic) return;
      try {
        setLoading(true);
        const request = getValues();
        const response = await InventoryStatisticsApi.getStockValues(clinic.id, request.date);
        setError(null);
        setStatistics(response.data);
      } catch (err) {
        setError(await getGeneralError(err));
      } finally {
        setLoading(false);
      }
    };

    void getStatistics();
  }, [reloadStatistics, getValues, clinic]);

  const printOrDownload = async (isDownload: boolean) => {
    if (!clinic) return;
    setPdfLoading(true);
    try {
      const date = getValues().date;
      isDownload
        ? await downloadInventoryStockValuesPdf(clinic.id, date)
        : await printInventoryStockValuesPdf(clinic.id, date, () => {
            logger.info("printing");
          });
    } catch (err) {
      setError(await getGeneralError(err));
    } finally {
      setPdfLoading(false);
    }
  };

  return (
    <div className="w-full md:max-w-4xl space-y-6">
      <div className="flex">
        <h2 className="text-lg font-semibold leading-tight text-zinc-800 lg:text-xl dark:text-white">
          {strings.inventoryStockValue}
        </h2>
        <div className="ml-auto mr-4">
          <Tooltip content={strings.downloadInventoryStockValues} placement="top">
            <Button className="tw-link" disabled={pdfLoading} onClick={() => printOrDownload(true)} variant="icon">
              <ArrowDownTray />
            </Button>
          </Tooltip>
        </div>
        <div>
          <Tooltip content={strings.printInventoryStockValues} placement="top">
            <Button className="tw-link" disabled={pdfLoading} onClick={() => printOrDownload(false)} variant="icon">
              <Printer />
            </Button>
          </Tooltip>
        </div>
      </div>
      <FormProvider {...filterFormMethods}>
        <form>
          <div className="flex items-center space-x-4">
            <DatePickerInput
              control={control}
              labelOff
              name="date"
              onChange={() => {
                setReloadStatistics(!reloadStatistics);
              }}
            />
            <Select
              labelOff
              name="netOrGross"
              register={register}
              options={[
                { value: "net", title: strings.net },
                { value: "gross", title: strings.gross },
              ]}
            />
          </div>
        </form>
      </FormProvider>
      <AlertBox message={error} />
      {loading ? (
        <LoaderInline />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {netOrGross === "net" ? (
            <>
              <StatInfoTile
                value={
                  statistics && NumberFormatter.formatPrice(statistics.totalAcquisitionNetPrice, statistics.currency)
                }
                title={strings.inventoryStockTotalAcquisitionNetPrice}
              />
              <StatInfoTile
                value={statistics && NumberFormatter.formatPrice(statistics.totalSellingNetPrice, statistics.currency)}
                title={strings.inventoryStockTotalSaleNetPrice}
              />
            </>
          ) : (
            <>
              <StatInfoTile
                value={
                  statistics && NumberFormatter.formatPrice(statistics.totalAcquisitionGrossPrice, statistics.currency)
                }
                title={strings.inventoryStockTotalAcquisitionGrossPrice}
              />
              <StatInfoTile
                value={
                  statistics && NumberFormatter.formatPrice(statistics.totalSellingGrossPrice, statistics.currency)
                }
                title={strings.inventoryStockTotalSaleGrossPrice}
              />
            </>
          )}
        </div>
      )}
      <div />
    </div>
  );
};

export default InventoryStockValue;
